import React, { ReactElement } from 'react';
import {
  Select as MuiSelect,
  MenuItem,
  SelectChangeEvent,
  FormControl,
  InputLabel,
  FormHelperText,
  SxProps,
  Theme,
} from '@mui/material';
import { OverridableStyles } from '~/utils/types.ts';
import { Typography } from '../index.ts';
import { Color } from '~/theme/index.js';

// FIXME: we shouldn't allow `any` here
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type BaseSourceItem = Record<string, any>;
export interface SelectProps<SourceType extends BaseSourceItem = BaseSourceItem>
  extends OverridableStyles {
  name?: string;
  onChange?: (value: string) => void;

  source: Array<SourceType | string>;
  /**
   * @description When `source` is an array of objects, this determines the property
   * used for each options `value`. This also determines the string rendered for
   * each option if `dataDisplayValue` is not defined
   */
  dataValue?: keyof SourceType;
  /**
   * @description When `source` is an array of objects, this determines the property
   * used for each options `key`
   */
  dataKey?: keyof SourceType;
  /**
   * @description When `source` is an array of objects, this determines the property
   * used for what is displayed to the user. `dataValue` still determines the underlying
   * `value` which is used for selection.
   */
  dataDisplayValue?: keyof SourceType;
  disabled?: boolean;
  placeholder?: string;
  value?: string;
  fullWidth?: boolean;
  error?: string;
  label?: string;
  displayEmpty?: boolean;
  dataTestId?: string;
  variant?: 'text' | 'outlined';
  size?: 'small' | 'medium';
  color?: Color;
}

const textVariantSelectSx: SxProps<Theme> = {
  '&::before': {
    // hide the horizontal line along the bottom
    display: 'none',
  },
  '& .MuiSelect-select': {
    paddingBottom: 0,
    '&:focus': {
      backgroundColor: 'transparent',
    },
  },
};

const SelectRenderer: React.ForwardRefRenderFunction<
  HTMLSelectElement,
  SelectProps
> = (
  {
    onChange,
    source,
    dataValue,
    dataKey,
    dataDisplayValue,
    disabled,
    value,
    placeholder,
    styleOverrides,
    name,
    fullWidth,
    error,
    label,
    displayEmpty,
    dataTestId,
    size,
    color = 'secondary',
    variant,
  },
  ref,
) => {
  const handleChange = async (event: SelectChangeEvent): Promise<void> => {
    onChange && onChange(event.target.value);
  };

  const placeholderNode = (
    <Typography styleOverrides={{ color: 'rgba(0, 0, 0, 0.40)' }}>
      {placeholder}
    </Typography>
  );

  const selectSx: SxProps<Theme> = {
    ...(variant === 'text' ? textVariantSelectSx : null),
    ...styleOverrides,
  };

  return (
    <FormControl
      fullWidth={fullWidth}
      error={!!error}
      disabled={disabled}
      size={size}
      color={color}
      variant={variant === 'text' ? 'standard' : variant}
    >
      {label && (
        <InputLabel data-test-id={`input-label-${label}`}>{label}</InputLabel>
      )}
      <MuiSelect
        error={!!error}
        name={name}
        color={color}
        labelId={`input-label-${label}`}
        onChange={handleChange}
        disabled={disabled}
        sx={selectSx}
        defaultValue={''}
        displayEmpty={displayEmpty}
        value={value}
        label={label}
        ref={ref}
        fullWidth={fullWidth}
        data-test-id={dataTestId || `select-${placeholder}`}
        size={size}
      >
        {placeholder ? (
          <MenuItem key={''} disabled value="">
            {placeholderNode}
          </MenuItem>
        ) : null}
        {source &&
          source.map((v) => {
            let valueText = '';
            const isObject = typeof v === 'object';
            if (isObject) {
              if (dataDisplayValue) {
                valueText = v[dataDisplayValue];
              } else if (dataValue) {
                valueText = v[dataValue];
              }
            } else {
              valueText = v;
            }

            const keyValue = isObject && dataKey ? v[dataKey] : v;
            return (
              <MenuItem
                sx={{ width: '100%', maxWidth: '500px' }}
                key={keyValue}
                value={keyValue}
              >
                {valueText}
              </MenuItem>
            );
          })}
      </MuiSelect>
      {error && <FormHelperText>{error}</FormHelperText>}
    </FormControl>
  );
};

const Select = React.forwardRef<HTMLSelectElement, SelectProps>(SelectRenderer);

Select.displayName = 'Select';

// casting is needed to let the component props take a generic type
// https://fettblog.eu/typescript-react-generic-forward-refs/
export default Select as <T extends BaseSourceItem = BaseSourceItem>(
  props: SelectProps<T> & React.RefAttributes<HTMLSelectElement>,
) => ReactElement;
